// src/pages/OrdersStats.jsx
import React, { useEffect, useState } from "react";
import { API } from "../api";

const OrdersStats = () => {
  const [paniers, setPaniers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("authToken");
  const axiosConfig = {
    headers: { Authorization: token ? `Bearer ${token}` : "" },
  };

  const fetchAllPaniers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await API.get("/panier/getPanier", axiosConfig);
      setPaniers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPaniers();
  }, []);

  // Fonction pour calculer les statistiques pour une période donnée
  const calcStatsForPeriod = (filteredPaniers) => {
    const paniersValidés = filteredPaniers.filter((p) => p.etat === 2);
    const chiffreAffaire = paniersValidés.reduce(
      (s, p) => s + (Number(p.valeur) || 0),
      0
    );
    const nombreCommandes = paniersValidés.length;
    const nombreArticlesVendus = paniersValidés.reduce((s, p) => {
      if (!Array.isArray(p.Appartenirs)) return s;
      return (
        s + p.Appartenirs.reduce((ss, a) => ss + (Number(a.quantite) || 0), 0)
      );
    }, 0);
    return { chiffreAffaire, nombreCommandes, nombreArticlesVendus };
  };

  // Fonction pour calculer le top 10 des produits pour une période
  const computeTopSoldForPeriod = (filteredPaniers) => {
    const map = new Map();
    const soldPaniers = filteredPaniers.filter((p) => p.etat === 2);

    for (const p of soldPaniers) {
      if (!Array.isArray(p.Appartenirs)) continue;
      for (const line of p.Appartenirs) {
        const art = line.Article;
        if (!art) continue;
        const id = art.id_art || art.id;
        const prev = map.get(id) || {
          id,
          nom: art.nom,
          marqueId: art.id_marq,
          sold: 0,
        };
        prev.sold += Number(line.quantite) || 0;
        map.set(id, prev);
      }
    }
    return Array.from(map.values())
      .sort((a, b) => b.sold - a.sold)
      .slice(0, 10);
  };

  // Obtenir les dates des 3 derniers mois
  const getMonthRanges = () => {
    const now = new Date();
    const currentMonth = {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
      name: now.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
    };

    const lastMonth = {
      start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
      name: new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        1
      ).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
    };

    const twoMonthsAgo = {
      start: new Date(now.getFullYear(), now.getMonth() - 2, 1),
      end: new Date(now.getFullYear(), now.getMonth() - 1, 0, 23, 59, 59),
      name: new Date(
        now.getFullYear(),
        now.getMonth() - 2,
        1
      ).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
    };

    return { currentMonth, lastMonth, twoMonthsAgo };
  };

  const { currentMonth, lastMonth, twoMonthsAgo } = getMonthRanges();

  // Filtrer les paniers par période
  const filterByMonth = (start, end) => {
    return paniers.filter((p) => {
      const date = new Date(p.createdAt || p.updatedAt);
      return date >= start && date <= end;
    });
  };

  const currentMonthPaniers = filterByMonth(
    currentMonth.start,
    currentMonth.end
  );
  const lastMonthPaniers = filterByMonth(lastMonth.start, lastMonth.end);
  const twoMonthsAgoPaniers = filterByMonth(
    twoMonthsAgo.start,
    twoMonthsAgo.end
  );

  // Statistiques globales (tous les paniers avec etat === 2)
  const globalStats = calcStatsForPeriod(paniers);
  const globalTopSold = computeTopSoldForPeriod(paniers);

  // Statistiques par mois
  const currentMonthStats = calcStatsForPeriod(currentMonthPaniers);
  const currentMonthTopSold = computeTopSoldForPeriod(currentMonthPaniers);

  const lastMonthStats = calcStatsForPeriod(lastMonthPaniers);
  const lastMonthTopSold = computeTopSoldForPeriod(lastMonthPaniers);

  const twoMonthsAgoStats = calcStatsForPeriod(twoMonthsAgoPaniers);
  const twoMonthsAgoTopSold = computeTopSoldForPeriod(twoMonthsAgoPaniers);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-3">Chargement des statistiques...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">❌ {error}</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center gap-2 mb-4">
        <i className="bi bi-graph-up-arrow fs-2 text-primary"></i>
        <h1 className="mb-0">Statistiques de Ventes</h1>
      </div>

      {/* Statistiques globales */}
      <div
        className="card mb-4 shadow-lg border-0"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <div className="card-body text-white p-4">
          <h2 className="card-title mb-4">
            <i className="bi bi-bar-chart-fill me-2"></i>
            Statistiques Globales
          </h2>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="bg-white bg-opacity-25 rounded p-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-currency-dollar fs-4"></i>
                  <small className="opacity-75">Chiffre d'affaires total</small>
                </div>
                <h3 className="mb-0">
                  {globalStats.chiffreAffaire.toLocaleString()} FCFA
                </h3>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bg-white bg-opacity-25 rounded p-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-cart-check fs-4"></i>
                  <small className="opacity-75">Total commandes</small>
                </div>
                <h3 className="mb-0">{globalStats.nombreCommandes}</h3>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bg-white bg-opacity-25 rounded p-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-box-seam fs-4"></i>
                  <small className="opacity-75">Total articles vendus</small>
                </div>
                <h3 className="mb-0">{globalStats.nombreArticlesVendus}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top 10 global */}
      <div className="card mb-5 shadow">
        <div className="card-header bg-warning bg-opacity-25">
          <h3 className="mb-0">
            <i className="bi bi-trophy-fill text-warning me-2"></i>
            Top 10 Produits les Plus Vendus (Global)
          </h3>
        </div>
        <div className="card-body">
          {globalTopSold.length === 0 ? (
            <p className="text-muted text-center py-4">Aucun article vendu</p>
          ) : (
            <div className="row g-3">
              {globalTopSold.map((product, index) => (
                <div key={product.id} className="col-md-6">
                  <div className="card h-100 shadow-sm border-0 hover-shadow">
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-3 flex-grow-1">
                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold`}
                          style={{
                            width: "45px",
                            height: "45px",
                            backgroundColor:
                              index === 0
                                ? "#ffc107"
                                : index === 1
                                ? "#6c757d"
                                : index === 2
                                ? "#fd7e14"
                                : "#0d6efd",
                          }}
                        >
                          {index + 1}
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{product.nom}</h6>
                          <small className="text-muted">
                            Marque: {product.marqueId || "N/A"}
                          </small>
                        </div>
                      </div>
                      <div className="text-end">
                        <h4 className="mb-0 text-primary">{product.sold}</h4>
                        <small className="text-muted">vendus</small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sections mensuelles */}
      <h2 className="mb-4">
        <i className="bi bi-calendar3 me-2"></i>
        Statistiques Mensuelles
      </h2>

      {/* Mois en cours */}
      <div className="card mb-4 shadow">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">
            <i className="bi bi-calendar-check me-2"></i>
            {currentMonth.name.charAt(0).toUpperCase() +
              currentMonth.name.slice(1)}{" "}
            (Mois en cours)
          </h4>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-primary h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-cash-coin text-success fs-4"></i>
                    <small className="text-muted">Chiffre d'affaires</small>
                  </div>
                  <h5 className="mb-0">
                    {currentMonthStats.chiffreAffaire.toLocaleString()} FCFA
                  </h5>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-primary h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-cart3 text-primary fs-4"></i>
                    <small className="text-muted">Commandes</small>
                  </div>
                  <h5 className="mb-0">{currentMonthStats.nombreCommandes}</h5>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-primary h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-boxes text-warning fs-4"></i>
                    <small className="text-muted">Articles vendus</small>
                  </div>
                  <h5 className="mb-0">
                    {currentMonthStats.nombreArticlesVendus}
                  </h5>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-light rounded p-3">
            <h6 className="mb-3">
              <i className="bi bi-star-fill text-primary me-2"></i>
              Top 10 produits du mois
            </h6>
            {currentMonthTopSold.length === 0 ? (
              <p className="text-muted text-center">
                Aucun article vendu ce mois
              </p>
            ) : (
              <ul className="list-group">
                {currentMonthTopSold.map((product, index) => (
                  <li
                    key={product.id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge bg-primary rounded-circle"
                        style={{
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {index + 1}
                      </span>
                      <div>
                        <strong>{product.nom}</strong>
                        <small className="d-block text-muted">
                          Marque: {product.marqueId || "N/A"}
                        </small>
                      </div>
                    </div>
                    <span className="badge bg-primary rounded-pill fs-6">
                      {product.sold}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Mois précédent */}
      <div className="card mb-4 shadow">
        <div className="card-header bg-secondary text-white">
          <h4 className="mb-0">
            <i className="bi bi-calendar-event me-2"></i>
            {lastMonth.name.charAt(0).toUpperCase() + lastMonth.name.slice(1)}
          </h4>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-secondary h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-cash-coin text-success fs-4"></i>
                    <small className="text-muted">Chiffre d'affaires</small>
                  </div>
                  <h5 className="mb-0">
                    {lastMonthStats.chiffreAffaire.toLocaleString()} FCFA
                  </h5>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-secondary h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-cart3 text-primary fs-4"></i>
                    <small className="text-muted">Commandes</small>
                  </div>
                  <h5 className="mb-0">{lastMonthStats.nombreCommandes}</h5>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-secondary h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-boxes text-warning fs-4"></i>
                    <small className="text-muted">Articles vendus</small>
                  </div>
                  <h5 className="mb-0">
                    {lastMonthStats.nombreArticlesVendus}
                  </h5>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-light rounded p-3">
            <h6 className="mb-3">
              <i className="bi bi-star-fill text-secondary me-2"></i>
              Top 10 produits du mois
            </h6>
            {lastMonthTopSold.length === 0 ? (
              <p className="text-muted text-center">
                Aucun article vendu ce mois
              </p>
            ) : (
              <ul className="list-group">
                {lastMonthTopSold.map((product, index) => (
                  <li
                    key={product.id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge bg-secondary rounded-circle"
                        style={{
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {index + 1}
                      </span>
                      <div>
                        <strong>{product.nom}</strong>
                        <small className="d-block text-muted">
                          Marque: {product.marqueId || "N/A"}
                        </small>
                      </div>
                    </div>
                    <span className="badge bg-secondary rounded-pill fs-6">
                      {product.sold}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Il y a 2 mois */}
      <div className="card mb-4 shadow">
        <div className="card-header bg-info text-white">
          <h4 className="mb-0">
            <i className="bi bi-calendar2-week me-2"></i>
            {twoMonthsAgo.name.charAt(0).toUpperCase() +
              twoMonthsAgo.name.slice(1)}
          </h4>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-info h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-cash-coin text-success fs-4"></i>
                    <small className="text-muted">Chiffre d'affaires</small>
                  </div>
                  <h5 className="mb-0">
                    {twoMonthsAgoStats.chiffreAffaire.toLocaleString()} FCFA
                  </h5>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-info h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-cart3 text-primary fs-4"></i>
                    <small className="text-muted">Commandes</small>
                  </div>
                  <h5 className="mb-0">{twoMonthsAgoStats.nombreCommandes}</h5>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-info h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-boxes text-warning fs-4"></i>
                    <small className="text-muted">Articles vendus</small>
                  </div>
                  <h5 className="mb-0">
                    {twoMonthsAgoStats.nombreArticlesVendus}
                  </h5>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-light rounded p-3">
            <h6 className="mb-3">
              <i className="bi bi-star-fill text-info me-2"></i>
              Top 10 produits du mois
            </h6>
            {twoMonthsAgoTopSold.length === 0 ? (
              <p className="text-muted text-center">
                Aucun article vendu ce mois
              </p>
            ) : (
              <ul className="list-group">
                {twoMonthsAgoTopSold.map((product, index) => (
                  <li
                    key={product.id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge bg-info rounded-circle"
                        style={{
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {index + 1}
                      </span>
                      <div>
                        <strong>{product.nom}</strong>
                        <small className="d-block text-muted">
                          Marque: {product.marqueId || "N/A"}
                        </small>
                      </div>
                    </div>
                    <span className="badge bg-info rounded-pill fs-6">
                      {product.sold}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Bouton de rafraîchissement */}
      <div className="text-center mt-4">
        <button className="btn btn-primary btn-lg" onClick={fetchAllPaniers}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Rafraîchir les données
        </button>
      </div>
    </div>
  );
};

export default OrdersStats;
